import { useCallback, useState } from "react";
import { checkDuplicates } from "../api/rfx-document-api";
import { checkTemplateDuplicates } from "../api/rfx-template-document-api";
import { CheckDuplicatesResult } from "../types/rfx-document.types";

interface DuplicateState {
  isChecking: boolean;
  conflicts: CheckDuplicatesResult["duplicates"];
  pendingFiles: File[];
}

export const useDuplicateDetection = (
  id: number,
  { isTemplate = false }: { isTemplate?: boolean } = {}
) => {
  const [state, setState] = useState<DuplicateState>({
    isChecking: false,
    conflicts: [],
    pendingFiles: [],
  });

  const checkFiles = useCallback(
    async (files: File[]): Promise<boolean> => {
      if (files.length === 0) {
        return false;
      }

      setState((prev) => ({ ...prev, isChecking: true, pendingFiles: files }));

      try {
        const fileNames = files.map((f) => f.name);
        const result = isTemplate
          ? await checkTemplateDuplicates(id, fileNames)
          : await checkDuplicates(id, fileNames);
        // Backend may return `null` / omit the array if no duplicates — coerce
        // to [] so downstream `.length` / `.map` calls are always safe.
        const duplicates = result?.duplicates ?? [];

        setState((prev) => ({
          ...prev,
          isChecking: false,
          conflicts: duplicates,
        }));

        return duplicates.length > 0;
      } catch {
        setState((prev) => ({ ...prev, isChecking: false, conflicts: [] }));
        return false;
      }
    },
    [id, isTemplate]
  );

  const clearConflicts = useCallback(() => {
    setState({ isChecking: false, conflicts: [], pendingFiles: [] });
  }, []);

  return {
    isChecking: state.isChecking,
    conflicts: state.conflicts,
    pendingFiles: state.pendingFiles,
    checkFiles,
    clearConflicts,
    hasConflicts: (state.conflicts?.length ?? 0) > 0,
  };
};
